import React, { useEffect, useState } from 'react';
import {
  Modal, Box, Typography, Chip, CircularProgress,
  Avatar, IconButton, Tooltip,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  ContentCopy as CopyIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  AccountTree as ProjectIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AccessTime as ClockIcon,
  History as HistoryIcon,
  GetApp as DownloadIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  Comment as CommentIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import XLSX from '../utils/excelUtils';
import moment from 'moment';
import axios from 'axios';
import { db_url } from '../Api/BaseUrl';
import { useSelector, useDispatch } from 'react-redux';
import { Get_All_Lead_Assigned_Data_Action } from '../Redux/Actions/Lead_Assigned_Action';

const C = {
  accent:      '#0D47A1',
  accentSoft:  'rgba(13,71,161,0.07)',
  text:        '#1C2340',
  textMuted:   '#7B829A',
  border:      '#E8EAF0',
  bg:          '#F7F8FC',
  white:       '#FFFFFF',
  success:     '#12B76A',
  successSoft: 'rgba(18,183,106,0.09)',
  warning:     '#F79009',
  warnSoft:    'rgba(247,144,9,0.09)',
  danger:      '#F04438',
  dangerSoft:  'rgba(240,68,56,0.08)',
  purple:      '#0D47A1',
  purpleSoft:  'rgba(124,58,237,0.08)',
  teal:        '#06B6D4',
  tealSoft:    'rgba(6,182,212,0.08)',
};

const getInitials = (name = '') =>
  (name || '').split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';

const AVATAR_COLORS = [C.accent, '#10B981', '#F59E0B', '#EF4444', '#0D47A1', '#06B6D4', '#EC4899'];
const getAvatarColor = (name = '') => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

const fmt = (d) => d ? moment(d).format('DD/MM/YYYY hh:mm A') : '—';

const fmtDur = (ms) => {
  if (isNaN(ms) || ms < 0) return '—';
  const s = Math.floor(ms / 1000), m = Math.floor(s / 60), hr = Math.floor(m / 60), d = Math.floor(hr / 24);
  return [d > 0 && `${d}d`, hr % 24 > 0 && `${hr % 24}h`, m % 60 > 0 && `${m % 60}m`, s % 60 > 0 && `${s % 60}s`].filter(Boolean).join(' ') || '0s';
};

const Card = ({ children, sx = {} }) => (
  <Box sx={{ bgcolor: C.white, border: `1px solid ${C.border}`, borderRadius: '14px', p: '18px 20px', ...sx }}>
    {children}
  </Box>
);

const SecHead = ({ icon: Icon, label, badge, right, color = C.accent }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '14px' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: `${color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon sx={{ fontSize: 15, color }} />
      </Box>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.text, fontFamily: "'DM Sans',sans-serif" }}>{label}</Typography>
      {badge}
    </Box>
    {right}
  </Box>
);

const InfoCell = ({ icon: Icon, label, value, color = C.accent, extra }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: '10px 12px', borderRadius: '10px', '&:hover': { bgcolor: C.accentSoft }, transition: 'background 0.12s' }}>
    <Box sx={{ width: 28, height: 28, borderRadius: '7px', bgcolor: `${color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: '1px' }}>
      <Icon sx={{ fontSize: 14, color }} />
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: 10, fontWeight: 600, color: C.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: "'DM Sans',sans-serif" }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: '1px' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 500, color: C.text, fontFamily: "'DM Sans',sans-serif", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || '—'}
        </Typography>
        {extra}
      </Box>
    </Box>
  </Box>
);

const Badge2 = ({ label, color = C.accent, bg }) => (
  <Box sx={{ display: 'inline-flex', px: '8px', py: '2px', borderRadius: '20px', fontSize: 10, fontWeight: 700, color, bgcolor: bg || `${color}12`, fontFamily: "'DM Sans',sans-serif" }}>
    {label}
  </Box>
);

const TH_SX = { fontSize: 10, fontWeight: 700, color: C.textMuted, letterSpacing: '0.07em', textTransform: 'uppercase', borderBottom: `1px solid ${C.border}`, py: '9px', px: '12px', fontFamily: "'DM Sans',sans-serif", bgcolor: C.bg };
const TD_SX = { fontSize: 12, color: C.text, fontFamily: "'DM Sans',sans-serif", py: '9px', px: '12px', borderBottom: `1px solid ${C.border}` };

export const PreviewLeadModal = ({ show, onHide, lead, loading }) => {
  const dispatch = useDispatch();
  const [assignLoading, setAssignLoading] = useState(false);
  const [stagesLoading, setStagesLoading] = useState(false);
  const [stagesData,    setStagesData]    = useState([]);

  const resleadassigned   = useSelector(s => s.AllLead_Assigned.Lead_Assigned);
  const LeadAssignedarray = Array.isArray(resleadassigned?.data) ? resleadassigned.data : [];

  useEffect(() => {
    if (!show || !lead?.leadId) return;

    // assigned history
    setAssignLoading(true);
    dispatch(Get_All_Lead_Assigned_Data_Action(lead.leadId))
      .finally(() => setAssignLoading(false));

    // stages
    setStagesData([]);
    setStagesLoading(true);
    axios.post(`${db_url}api/v1/LeadAssigned/get-assigned-by-leads`, { leadIds: [lead.leadId] })
      .then(res => {
        console.log('✅ stages response:', JSON.stringify(res.data, null, 2));
        setStagesData(res.data?.data || []);
      })
      .catch(err => {
        console.log('❌ stages error:', err);
        setStagesData([]);
      })
      .finally(() => setStagesLoading(false));

  }, [show, lead?.leadId, dispatch]);

  const history = (() => {
    if (!Array.isArray(LeadAssignedarray) || !lead?.leadId) return [];
    return LeadAssignedarray
      .filter(a => (a.LeadId?._id || a.LeadId) === lead.leadId)
      .sort((a, b) => new Date(b.date_Assigned || b.createdAt) - new Date(a.date_Assigned || a.createdAt))
      .map((a, i) => ({ id: a._id || i, to: a.Assigned_to?.name || '—', from: a.Assigned_From?.name || '—', date: a.assignDateTime || a.createdAt }));
  })();

  const exportXls = () => {
    const ws = XLSX.utils.json_to_sheet(history.map(a => ({ 'Sales Name': a.to, 'From': a.from, 'Date': fmt(a.date) })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Assignments');
    XLSX.writeFile(wb, `assignments_${lead?.name || 'lead'}.xlsx`);
  };

  if (loading) return (
    <Modal open={show} onClose={onHide} sx={{ backdropFilter: 'blur(6px)' }}>
      <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <CircularProgress sx={{ color: C.accent }} size={44} />
        <Typography sx={{ color: '#fff', fontFamily: "'DM Sans',sans-serif", fontSize: 13 }}>Loading…</Typography>
      </Box>
    </Modal>
  );

  if (!lead) return null;

  const totalStagesCount = stagesData.reduce((acc, s) =>
    acc + s.leads.reduce((a, l) => a + (l.stages?.length || 0), 0), 0
  );

  return (
    <Modal open={show} onClose={onHide} sx={{ backdropFilter: 'blur(8px)', backgroundColor: 'rgba(15,17,23,0.6)' }}>
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        width: '94%', maxWidth: 1080, maxHeight: '90vh',
        overflowY: 'auto', overflowX: 'hidden',
        bgcolor: C.bg, borderRadius: '20px',
        boxShadow: '0 24px 64px rgba(16,24,40,0.22)',
        fontFamily: "'DM Sans',sans-serif",
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': { width: 5 },
        '&::-webkit-scrollbar-thumb': { bgcolor: C.border, borderRadius: 99 },
      }}>

        {/* STICKY HEADER */}
        <Box sx={{
          position: 'sticky', top: 0, zIndex: 10,
          bgcolor: C.white, borderBottom: `1px solid ${C.border}`,
          px: '24px', py: '14px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: C.accentSoft, color: C.accent, fontSize: 13, fontWeight: 800 }}>
              {getInitials(lead.name)}
            </Avatar>
            <Box>
              <Typography sx={{ fontSize: 15, fontWeight: 800, color: C.text, lineHeight: 1.2, letterSpacing: '-0.2px', fontFamily: "'DM Sans',sans-serif" }}>{lead.name}</Typography>
              <Typography sx={{ fontSize: 11, color: C.textMuted, fontFamily: "'DM Sans',sans-serif" }}>Lead Preview</Typography>
            </Box>
            {lead._id && (
              <Chip label={`ID: ${lead._id.slice(-6)}`} size="small"
                sx={{ fontSize: 10, fontWeight: 700, bgcolor: C.accentSoft, color: C.accent, border: 'none' }} />
            )}
          </Box>
          <IconButton size="small" onClick={onHide}
            sx={{ color: C.textMuted, borderRadius: '8px', '&:hover': { color: C.danger, bgcolor: C.dangerSoft }, transition: 'all 0.15s' }}>
            <CloseIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Box>

        {/* BODY */}
        <Box sx={{ p: '18px 20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>

          {/* ROW 1 */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <Card>
              <SecHead icon={PersonIcon} label="Basic Information" />
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px' }}>
                <InfoCell icon={PersonIcon}   label="Name"       value={lead.name}                          color={C.accent}  />
                <InfoCell icon={PhoneIcon}    label="Phone"      value={lead.phone}                         color={C.success}
                  extra={
                    <Box sx={{ display: 'flex' }}>
                      <Tooltip title="Copy"><IconButton size="small" onClick={() => navigator.clipboard.writeText(lead.phone)}><CopyIcon sx={{ fontSize: 11 }} /></IconButton></Tooltip>
                      <Tooltip title="WhatsApp"><IconButton size="small" href={`https://wa.me/${lead.phone}`} target="_blank"><WhatsAppIcon sx={{ fontSize: 11, color: '#25D366' }} /></IconButton></Tooltip>
                    </Box>
                  }
                />
                <InfoCell icon={EmailIcon}    label="Email"      value={lead.email || 'Not provided'}       color={C.teal}    />
                <InfoCell icon={ProjectIcon}  label="Project"    value={lead.project}                       color={C.warning} />
                <InfoCell icon={BusinessIcon} label="Developer"  value={lead?.developer?.name}              color={C.purple}  />
                <InfoCell icon={CalendarIcon} label="Created At" value={moment(lead.createdAt).format('DD/MM/YYYY hh:mm A')} color={C.danger} />
              </Box>
            </Card>

            <Card>
              <SecHead
                icon={HistoryIcon} label="Assignment History"
                badge={
                  assignLoading
                    ? <CircularProgress size={13} sx={{ color: C.accent, ml: 1 }} />
                    : <Chip label={history.length} size="small" sx={{ fontSize: 10, fontWeight: 700, bgcolor: C.accentSoft, color: C.accent, border: 'none', height: 20, ml: 0.5 }} />
                }
                right={
                  history.length > 0 && (
                    <Tooltip title="Export Excel">
                      <IconButton size="small" onClick={exportXls}
                        sx={{ color: C.accent, bgcolor: C.accentSoft, borderRadius: '8px', '&:hover': { bgcolor: C.accent, color: '#fff' }, transition: 'all 0.15s' }}>
                        <DownloadIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  )
                }
              />
              <Box sx={{ display: 'flex', gap: '10px', mb: '14px' }}>
                {[
                  { label: 'Total',   val: history.length,                                      color: C.accent  },
                  { label: 'Unique',  val: new Set(history.map(a => a.to)).size,                color: C.teal    },
                  { label: 'Current', val: history[0]?.to?.split(' ')[0] || '—',                color: C.success },
                  { label: 'First',   val: history[history.length - 1]?.to?.split(' ')[0] || '—', color: C.warning },
                ].map(({ label, val, color }) => (
                  <Box key={label} sx={{ flex: 1, textAlign: 'center', p: '10px 8px', borderRadius: '10px', border: `1px solid ${C.border}`, bgcolor: C.white }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color, lineHeight: 1, fontFamily: "'DM Sans',sans-serif" }}>{val}</Typography>
                    <Typography sx={{ fontSize: 9, fontWeight: 600, color: C.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: "'DM Sans',sans-serif", mt: '3px' }}>{label}</Typography>
                  </Box>
                ))}
              </Box>
              {assignLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} sx={{ color: C.accent }} /></Box>
              ) : history.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <HistoryIcon sx={{ fontSize: 32, color: C.border, mb: 1 }} />
                  <Typography sx={{ fontSize: 12, color: C.textMuted, fontFamily: "'DM Sans',sans-serif" }}>No history yet</Typography>
                </Box>
              ) : (
                <Box sx={{ overflowX: 'auto', maxHeight: 210, overflowY: 'auto', borderRadius: '10px', border: `1px solid ${C.border}` }}>
                  <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 400 }}>
                    <TableHead>
                      <TableRow>
                        {['Assigned To', 'Action Done By', 'Date'].map(h => (
                          <TableCell key={h} sx={TH_SX}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {history.map((a, i) => (
                        <TableRow key={a.id} sx={{ '&:hover': { bgcolor: C.accentSoft }, transition: 'background 0.1s' }}>
                          <TableCell sx={TD_SX}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                              <Avatar sx={{ width: 22, height: 22, bgcolor: getAvatarColor(a.to), fontSize: 9, fontWeight: 700 }}>{getInitials(a.to)}</Avatar>
                              <Typography sx={{ fontSize: 12, fontWeight: i === 0 ? 700 : 400, color: C.text, fontFamily: "'DM Sans',sans-serif" }}>{a.to}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ ...TD_SX, color: C.textMuted }}>{a.from}</TableCell>
                          <TableCell sx={{ ...TD_SX, color: C.textMuted, whiteSpace: 'nowrap', fontSize: 11 }}>{fmt(a.date)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Card>
          </Box>

          {/* ROW 2 */}
          {lead.salesAssignDurations?.length > 0 && (
            <Card>
              <SecHead icon={ScheduleIcon} label="Lead With Sales (Period)"
                badge={<Chip label={`${lead.salesAssignDurations.length} sales`} size="small" sx={{ fontSize: 10, fontWeight: 700, bgcolor: C.accentSoft, color: C.accent, border: 'none', height: 20, ml: 0.5 }} />}
              />
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px' }}>
                {lead.salesAssignDurations.map((sales, idx) => (
                  <Box key={idx} sx={{ border: `1px solid ${C.border}`, borderRadius: '12px', overflow: 'hidden' }}>
                    <Box sx={{ px: '14px', py: '10px', bgcolor: C.accentSoft, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Avatar sx={{ width: 26, height: 26, bgcolor: getAvatarColor(sales.name), fontSize: 10, fontWeight: 700 }}>{getInitials(sales.name)}</Avatar>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.text, fontFamily: "'DM Sans',sans-serif" }}>{sales.name}</Typography>
                      </Box>
                      <Chip label={`${sales.periods?.length || 0} periods`} size="small" sx={{ fontSize: 9, fontWeight: 600, bgcolor: C.accent, color: '#fff', border: 'none', height: 18 }} />
                    </Box>
                    <Box sx={{ px: '14px', py: '8px', display: 'flex', alignItems: 'center', gap: '6px', borderBottom: `1px solid ${C.border}` }}>
                      <ClockIcon sx={{ fontSize: 12, color: C.textMuted }} />
                      <Typography sx={{ fontSize: 11, color: C.textMuted, fontFamily: "'DM Sans',sans-serif" }}>Total: <strong style={{ color: C.text }}>{sales.totalDuration}</strong></Typography>
                    </Box>
                    <Box sx={{ px: '12px', py: '10px', display: 'flex', flexDirection: 'column', gap: '7px', maxHeight: 180, overflowY: 'auto' }}>
                      {sales.periods?.map((p, i) => (
                        <Box key={i} sx={{ p: '9px 12px', borderRadius: '8px', bgcolor: C.bg, border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                          <Box>
                            <Typography sx={{ fontSize: 10, color: C.textMuted, fontFamily: "'DM Sans',sans-serif" }}>From: <strong style={{ color: C.text }}>{moment(p.from).format('DD/MM/YY hh:mm A')}</strong></Typography>
                            <Typography sx={{ fontSize: 10, color: C.textMuted, fontFamily: "'DM Sans',sans-serif" }}>To:&nbsp;&nbsp;&nbsp; <strong style={{ color: C.text }}>{moment(p.to).format('DD/MM/YY hh:mm A')}</strong></Typography>
                          </Box>
                          <Badge2 label={p.durationStr} color={C.teal} bg={C.tealSoft} />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Card>
          )}

          {/* ROW 3: Stages History */}
          <Card>
            <SecHead
              icon={AssignmentIcon}
              label="Stages History"
              badge={
                stagesLoading
                  ? <CircularProgress size={13} sx={{ color: C.accent, ml: 1 }} />
                  : <Chip label={`${totalStagesCount} stages`} size="small" sx={{ fontSize: 10, fontWeight: 700, bgcolor: C.accentSoft, color: C.accent, border: 'none', height: 20, ml: 0.5 }} />
              }
            />

            {stagesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={22} sx={{ color: C.accent }} />
              </Box>
            ) : totalStagesCount === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <AssignmentIcon sx={{ fontSize: 32, color: C.border, mb: 1 }} />
                <Typography sx={{ fontSize: 12, color: C.textMuted, fontFamily: "'DM Sans',sans-serif" }}>No stages yet</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {stagesData.map((salesEntry) => {
                  const stages = salesEntry.leads?.flatMap(l => l.stages || []) || [];
                  if (!stages.length) return null;
                  return (
                    <Box key={salesEntry.salesId}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '10px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Avatar sx={{ width: 26, height: 26, bgcolor: getAvatarColor(salesEntry.salesName), fontSize: 10, fontWeight: 700 }}>
                            {getInitials(salesEntry.salesName)}
                          </Avatar>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.text, fontFamily: "'DM Sans',sans-serif" }}>
                            {salesEntry.salesName}
                          </Typography>
                        </Box>
                        <Chip label={`${stages.length} stages`} size="small"
                          sx={{ fontSize: 10, fontWeight: 600, bgcolor: C.accentSoft, color: C.accent, border: 'none', height: 20 }} />
                      </Box>

                      <Box sx={{ borderRadius: '10px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {['#', 'Stage', 'Date', 'Selected At', 'Created At'].map(h => (
                                <TableCell key={h} sx={TH_SX}>{h}</TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {stages.map((stage, idx) => (
                              <TableRow key={stage.stageId || idx} sx={{ '&:hover': { bgcolor: C.accentSoft }, transition: 'background 0.1s' }}>
                                <TableCell sx={{ ...TD_SX, color: C.textMuted, width: 36 }}>{idx + 1}</TableCell>
                                <TableCell sx={TD_SX}>
                                  <Badge2
                                    label={stage.stageName || '—'}
                                    color={idx === stages.length - 1 ? C.success : C.accent}
                                    bg={idx === stages.length - 1 ? C.successSoft : C.accentSoft}
                                  />
                                </TableCell>
                                <TableCell sx={{ ...TD_SX, color: C.textMuted, fontSize: 11, whiteSpace: 'nowrap' }}>{fmt(stage.date)}</TableCell>
                                <TableCell sx={{ ...TD_SX, color: C.textMuted, fontSize: 11, whiteSpace: 'nowrap' }}>{fmt(stage.dateselectedforstage)}</TableCell>
                                <TableCell sx={{ ...TD_SX, color: C.textMuted, fontSize: 11, whiteSpace: 'nowrap' }}>{fmt(stage.createdAt)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Card>

          {/* ROW 4: Comments */}
          {lead.comments?.length > 0 && (
            <Card>
              <SecHead
                icon={CommentIcon} label="Comments"
                badge={<Chip label={lead.comments.length} size="small" sx={{ fontSize: 10, fontWeight: 700, bgcolor: C.purpleSoft, color: C.purple, border: 'none', height: 20, ml: 0.5 }} />}
                color={C.purple}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {lead.comments.map((comment, idx) => (
                  <Box key={idx}>
                    <Box sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                      <Avatar
                        src={comment.sales?.profileImg ? `${db_url}/UsersImages/${comment.sales.profileImg}` : null}
                        sx={{ width: 36, height: 36, bgcolor: getAvatarColor(comment.sales?.name || ''), fontSize: 12, fontWeight: 700, flexShrink: 0 }}
                      >
                        {getInitials(comment.sales?.name)}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ bgcolor: C.bg, border: `1px solid ${C.border}`, borderRadius: '14px', p: '10px 14px' }}>
                          <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.text, fontFamily: "'DM Sans',sans-serif", mb: '4px' }}>
                            {comment.sales?.name}
                          </Typography>
                          <Typography sx={{ fontSize: 13, color: C.text, fontFamily: "'DM Sans',sans-serif", lineHeight: 1.6 }}>
                            {comment.firstcomment?.text}
                          </Typography>
                          {comment.secondcomment?.text && (
                            <Box sx={{ mt: '10px', p: '8px 12px', borderRadius: '9px', bgcolor: C.warnSoft, borderLeft: `3px solid ${C.warning}`, display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                              <Typography sx={{ fontSize: 10, fontWeight: 700, color: C.warning, letterSpacing: '0.05em', textTransform: 'uppercase', fontFamily: "'DM Sans',sans-serif", whiteSpace: 'nowrap', mt: '1px' }}>
                                Next Action
                              </Typography>
                              <Typography sx={{ fontSize: 12, color: C.text, fontFamily: "'DM Sans',sans-serif", lineHeight: 1.5 }}>
                                {comment.secondcomment.text}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mt: '5px', pl: '4px' }}>
                          <Typography sx={{ fontSize: 11, color: C.textMuted, fontFamily: "'DM Sans',sans-serif" }}>
                            {moment(comment.firstcomment?.date).format('DD/MM/YYYY · hh:mm A')}
                          </Typography>
                          {comment.replies?.length > 0 && (
                            <>
                              <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: C.border }} />
                              <Typography sx={{ fontSize: 11, color: C.textMuted, fontFamily: "'DM Sans',sans-serif" }}>
                                {comment.replies.length} {comment.replies.length === 1 ? 'Reply' : 'Replies'}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Box>
                    </Box>
                    {comment.replies?.length > 0 && (
                      <Box sx={{ ml: '46px', mt: '10px', position: 'relative' }}>
                        <Box sx={{ position: 'absolute', left: '-22px', top: 0, bottom: '16px', width: '2px', bgcolor: C.border, borderRadius: '2px' }} />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {comment.replies.map((reply, ri) => (
                            <Box key={ri} sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                              <Avatar
                                src={reply?.user?.profileImg ? `${db_url}/UsersImages/${reply.user.profileImg}` : null}
                                sx={{ width: 28, height: 28, bgcolor: getAvatarColor(reply?.user?.name || ''), fontSize: 10, fontWeight: 700, flexShrink: 0 }}
                              >
                                {getInitials(reply?.user?.name)}
                              </Avatar>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ bgcolor: C.bg, border: `1px solid ${C.border}`, borderRadius: '12px', p: '8px 12px' }}>
                                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.text, fontFamily: "'DM Sans',sans-serif", mb: '3px' }}>{reply?.user?.name}</Typography>
                                  <Typography sx={{ fontSize: 12, color: C.text, fontFamily: "'DM Sans',sans-serif", lineHeight: 1.5 }}>{reply.text}</Typography>
                                </Box>
                                <Typography sx={{ fontSize: 10, color: C.textMuted, fontFamily: "'DM Sans',sans-serif", mt: '4px', pl: '4px' }}>
                                  {moment(reply.date).format('DD/MM/YYYY · hh:mm A')}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Card>
          )}

        </Box>
      </Box>
    </Modal>
  );
};